<script lang="ts" module>
  import { onDestroy } from 'svelte';

  import type { AssistantInstruction, SubEvaluation } from '@/teoCore/TeoApi';
  import type { AnalysisResult } from '@/teoCore/text/AnalysisResult';
  import type { TeoText } from '@/teoCore/text/TeoText';
  import DoneCard from '@/ui/components/assistant/Performance/DoneCard/DoneCard.svelte';
  import InitCard from '@/ui/components/assistant/Performance/InitCard/InitCard.svelte';
  import InstructionCard from '@/ui/components/assistant/Performance/InstructionCard/InstructionCard.svelte';
  import ColumnHeadline from '@/ui/elements/column-headline/ColumnHeadline.svelte';
  import { useTeoTextUiStateObserver } from '@/utils/use-teo-event-observer.svelte';

  import AiComponent from './AIComponent.svelte';
  import AutoAiComponent from './AutoAIComponent.svelte';

  // import QuickAiComponent from './QuickAIComponent.svelte';

  export type AssistantInstructionExtended = AssistantInstruction & {
    sub_evaluation?: SubEvaluation;
  };
</script>

<script lang="ts">
  const { teoText }: { teoText: TeoText } = $props();
  let TS = $derived(teoText.store);

  let instruction: AssistantInstructionExtended | undefined = $state();
  let assistantState: 'init' | 'instructions' | 'done' = $state('init');
  let analysisLoading = $state(false);

  const dimension = useTeoTextUiStateObserver(teoText.ui, 'dimension');

  if (teoText.analysis) {
    onAnalysis(teoText.analysis);
  }

  teoText.events.addEventListener('gotAnalysis', onAnalysis);
  teoText.events.addEventListener('analysisPending', onAnalysisPending);
  onDestroy(() => {
    teoText.events.removeEventListener('gotAnalysis', onAnalysis);
    teoText.events.removeEventListener('analysisPending', onAnalysisPending);
  });

  $effect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    dimension.current;
    if (teoText.analysis && dimension.current) {
      assistantState = 'instructions';
      updateInstruction(teoText.analysis, dimension.current);
    } else if (teoText.analysis?.isGood()) {
      assistantState = 'done';
    }
  });

  $effect(() => {
    if (assistantState !== 'instructions' && teoText.ai.selection.isEnabled) {
      teoText.ai.selection.disable();
    }
  });

  function onAnalysisPending(pending: boolean) {
    analysisLoading = pending;
    if (pending) {
      assistantState = 'init';
    }
  }

  function onAnalysis(result: AnalysisResult) {
    updateInstruction(result);
    if (!result.isGood()) {
      assistantState = 'instructions';
    } else {
      teoText.ui.update('dimension', undefined);
      teoText.ui.update('textProperty', undefined);
      assistantState = 'done';
    }
    analysisLoading = false;
  }

  function updateInstruction(result: AnalysisResult, dimension?: string) {
    const instructionExt: AssistantInstructionExtended | undefined =
      result?.updateCurrentInstruction(dimension);
    if (instructionExt) {
      const indices = result.getIndices();
      if (instructionExt.index_name && indices[instructionExt.index_name]) {
        instructionExt.sub_evaluation = indices[instructionExt.index_name];
      } else {
        instructionExt.sub_evaluation = undefined;
      }
      instruction = instructionExt;
      teoText.ui.update('dimension', instructionExt.index_name);
    } else {
      instruction = undefined;
    }
  }
</script>

<div class="mb-0 ml-2 flex gap-1">
  <ColumnHeadline text="Performance-Pilot" />
</div>
{#if $TS.performanceMode === 'manual'}
  {#if assistantState === 'init'}
    <InitCard text={teoText} cardLabel="Deine Aufgabe" loading={analysisLoading}></InitCard>
  {:else if assistantState === 'instructions'}
    <div class="flex flex-col space-y-4">
      <InstructionCard cardLabel="Deine Aufgabe" {instruction}></InstructionCard>
      <AiComponent {teoText} withAutoMode></AiComponent>
    </div>
  {:else}
    <DoneCard cardLabel="Deine Aufgabe"></DoneCard>
  {/if}
{:else}
  <AutoAiComponent {teoText} {instruction}></AutoAiComponent>
{/if}
